import React, { useEffect } from 'react'
import { observer, useLocalObservable } from 'mobx-react-lite'
import { autorun } from 'mobx'
import clsx from 'clsx'

import { Box, FormHelperText, FormLabel, Grid, IconButton } from '@material-ui/core'

import addIcon from '../assets/addIcon.svg'
import arrowRight from '../assets/arrowRight.svg'
import deleteIcon from '../assets/deleteIcon.svg'

import { BootstrapInput } from './BootstrapInput'
import { WarehousePicker } from './WarehousePicker'
import { MoySkladStorePicker } from './MoySkladStorePicker'
import { StoresFormBaseProps, useStoresFormStyles } from './StoresFormDirect'

import { useLogic } from '../hooks/storeHook'
import { useMessages } from '../hooks/snackbarHooks'
import { useDefaultMenuItemStyles } from '../hooks/defaultSelectStylesHook'

import { required } from '../common/objectUtils'
import { integrationTypeNames } from '../types/integrationTypeUtils'

import { EditStoreModel } from '../server/mpsklad_core/Models/EditStoreModel'
import { StoreModelBase } from '../server/mpsklad_core/Models/StoreModelBase'
import { WarehouseModel } from '../server/mpsklad_core/Models/WarehouseModel'
import { IntegrationType } from '../server/mpsklad_core/Entity/IntegrationType'
import { MoySkladStoreModel } from '../server/mpsklad_core/Models/MoySkladStoreModel'
import { StoreRelationType } from '../server/mpsklad_core/Entity/Base/StoreRelationType'

export type StoresFormSplitMoySkladByDimensionsProps<
  TStore extends StoreModelBase, TEditStore extends EditStoreModel, TWarehouse extends WarehouseModel> =
  StoresFormBaseProps<TStore, TEditStore, TWarehouse>
  & {
  updateStoreCargoType: (editStore: TEditStore, warehouse: TWarehouse) => void

  filterWhByCargoType: (warehouse: TWarehouse, formStores: TEditStore[]) => boolean
}

/**
 * See {@link StoreRelationType.SplitMoySkladByDimensions}.
 */
export const StoresFormSplitMoySkladByDimensions =
  observer(
    <TStore extends StoreModelBase, TEditStore extends EditStoreModel, TWarehouse extends WarehouseModel>
    ({
       disabled, storeType,
       serverData, formData, initStoreCargoType, updateStoreCargoType,
       whKeySelector, storeWhKeySelector, filterWhByCargoType,
       formatWarehouseDisplayName
     }: StoresFormSplitMoySkladByDimensionsProps<TStore, TEditStore, TWarehouse>) => {
      const classes = useStoresFormStyles()
      const menuClasses = useDefaultMenuItemStyles()

      const {showWarning} = useMessages()

      const {showDialog} = useLogic()

      const localFormData: {msStoreId: string} =
        useLocalObservable(() => ({
          msStoreId: serverData.stores.length
                     ? serverData.stores[0].msStoreIds[0]
                     : serverData.msStores.length
                       ? serverData.msStores[0].id
                       : '' // Shouldn't be used, there is validation below (can't reorder hooks)
        }))

      useEffect(() =>
        autorun(() => {
          if (localFormData.msStoreId) {
            for (const store of formData.stores) {
              store.msStoreIds = [localFormData.msStoreId]
            }
          }
        }))

      const storeTypeName = integrationTypeNames[storeType]

      if (storeType !== IntegrationType.Ozon && storeType !== IntegrationType.Wildberries) {
        throw new Error(`Bad store type: ${storeType}!`)
      }

      if (formData.relationType !== StoreRelationType.SplitMoySkladByDimensions) {
        throw new Error(`Bad relation type: ${formData.relationType}!`)
      }

      if (serverData.warehouses.length === 0) {
        return <FormHelperText error>Нет складов {storeTypeName}.</FormHelperText>
      }

      if (serverData.msStores.length === 0) {
        return <FormHelperText error>Нет складов МоегоСклада.</FormHelperText>
      }

      const unmatchedWarehouses =
        serverData.warehouses.filter(
          warehouse => formData.stores.every(store => storeWhKeySelector(store) !== whKeySelector(warehouse))
                       && filterWhByCargoType(warehouse, formData.stores))

      const unmatchedWarehouse = unmatchedWarehouses.length ? unmatchedWarehouses[0] : undefined

      const onAddWarehouse = () => {
        if (!unmatchedWarehouse) {
          showWarning(`Все склады ${storeTypeName} уже сопоставлены!`)
          return
        }

        formData.stores.push(
          initStoreCargoType(unmatchedWarehouse, {
            storeId: undefined,
            warehouseId: unmatchedWarehouse.id,
            warehouseName: unmatchedWarehouse.name,
            msStoreIds: [localFormData.msStoreId]
          }))
      }

      const onDeleteStore =
        async (formStore: EditStoreModel) => {
          if (!await showDialog(
            `Заказы для склада ${storeTypeName} будут удалены из MPsklad после сохранения!`, {
              title: 'Удалить сопоставление складов?',
              acceptButton: 'Удалить'
            })) {
            return
          }

          formData.stores = formData.stores.filter(_ => _ !== formStore)
        }

      const onChangeWarehouse =
        (formStore: TEditStore, warehouse: TWarehouse) => {
          formStore.warehouseId = warehouse.id
          formStore.warehouseName = warehouse.name
          updateStoreCargoType(formStore, warehouse)
        }

      const onChangeMsStore =
        (newMsStore: MoySkladStoreModel | null) =>
          localFormData.msStoreId = required(newMsStore).id

      return (
        <Grid container direction="column">
          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <FormLabel component="legend" className={classes.label}>
                  <span className={classes.dash}>&mdash;</span>
                  Склад МоегоСклада
                </FormLabel>
              </Grid>

              <Grid item xs={6}>
                <FormLabel component="legend" className={classes.label}>
                  <span className={classes.dash}>&mdash;</span>
                  Склад {storeTypeName}
                </FormLabel>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} className={classes.storeRow}>
            <Grid container alignItems="center" spacing={3}>
              <Grid item xs={5}>
                <Grid container>
                  <Grid item xs={10}>
                    <MoySkladStorePicker
                      disabled={disabled}
                      msStores={serverData.msStores}
                      msStoreId={localFormData.msStoreId}
                      formData={formData}
                      onChange={onChangeMsStore}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={1}>
                <Box className={classes.arrowIconWrapper}>
                  <img src={arrowRight} alt="arrow-icon" className={classes.arrowIcon}/>
                </Box>
              </Grid>

              <Grid item xs={6}>
                {
                  formData.stores.map((formStore, index) =>
                    <Grid container spacing={3} key={index}>
                      <Grid item xs={8}>
                        <Box display="flex" alignItems="center" width="100%" style={{gap: '12px'}}>
                          <Box flex={1}>
                            <WarehousePicker
                              formStore={formStore}
                              formData={formData}
                              warehouses={serverData.warehouses /* don't filter, causes unselectable warehouses */}
                              whKeySelector={whKeySelector}
                              storeWhKeySelector={storeWhKeySelector}
                              formatWarehouseDisplayName={formatWarehouseDisplayName}
                              onChange={warehouse => onChangeWarehouse(formStore, warehouse)}
                            />
                          </Box>
                        </Box>
                      </Grid>

                      {/* Delete button */}
                      <Grid item xs={2}>
                        <IconButton
                          title="Удалить"
                          disabled={disabled}
                          className={clsx(menuClasses.iconButtonOutlined)}
                          onClick={() => onDeleteStore(formStore)}
                        >
                          <img src={deleteIcon} alt=""/>
                        </IconButton>
                      </Grid>
                    </Grid>
                  )}
              </Grid>
            </Grid>
          </Grid>

          {/* Add warehouse button */}
          {
            !!unmatchedWarehouse &&
            <Grid item xs={12} className={classes.storeRow}>
              <Grid container alignItems="center" spacing={3}>
                <Grid item xs={5}>
                  {/* spacer */}
                </Grid>

                <Grid item xs={1}>
                  {/* spacer */}
                </Grid>

                <Grid item xs={6}>
                  <Grid container spacing={3}>
                    <Grid item xs={8}>
                      <BootstrapInput
                        readOnly
                        title="Добавить"
                        className={clsx(menuClasses.input, menuClasses.inputClickable)}
                        onClick={onAddWarehouse}
                      />
                    </Grid>

                    <Grid item xs={2}>
                      <IconButton
                        title="Добавить"
                        disabled={disabled}
                        className={clsx(menuClasses.iconButtonOutlined)}
                        onClick={onAddWarehouse}
                      >
                        <img src={addIcon} alt=""/>
                      </IconButton>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          }
        </Grid>
      )
    })